// apps/fe/src/components/containers/catalog/catalog-container.tsx

'use client';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { createEventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { useRouter } from 'next/navigation';
import { CategoryTypes } from '@/catalog';
import {
  CatalogCategoryScreen,
  CatalogEvents,
  CatalogHomeScreen,
  CatalogSearchScreen,
  CatalogViewData,
  CatalogSearchInitialData,
  CatalogCategoryInitialData,
  CatalogPageType,
  CatalogEventKeys,
} from '@/catalog';
import { useAuthStore } from '@/auth';
import { useProfileStore } from '@/profile';
import Skeleton from '@mui/material/Skeleton';
import { CatalogAppWrapper } from 'src/modules/catalog/context/catalog-app-wrapper';
import { invalidateCaches } from 'src/modules/catalog/utils/cache-utils';
import { useUserStatus } from 'src/contexts/user-status-provider';
import { useNonEssentialCatalogData } from 'src/modules/catalog/hooks/use-non-essential-catalog-data';

interface CatalogContainerProps {
  dict: CatalogDict;
  type: CatalogPageType;
  initialData: CatalogViewData | CatalogSearchInitialData | CatalogCategoryInitialData;
  query?: string;
  categoryName?: CategoryTypes;
}

/**
 * Container component for all catalog screens.
 * Handles event bus creation, routing, and authentication state.
 */
export function CatalogContainer({
  dict,
  type,
  initialData,
  query,
  categoryName,
}: CatalogContainerProps) {
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);
  const [prevType, setPrevType] = useState<CatalogPageType | null>(null);

  // Create an event bus for the catalog scope
  const catalogEventBus = useMemo(() => createEventBus<CatalogEvents>('catalog-scope'), []);

  // Get authentication state
  const { user, setUser, setHydrated } = useAuthStore();
  const { profile, setProfile } = useProfileStore();
  const { isLoggedIn } = useUserStatus();

  // Fetch non-essential data for catalog home page - for ALL users, not just logged in
  const { data: nonEssentialData, isLoading: isLoadingNonEssential } = useNonEssentialCatalogData(
    type === 'home',
  );

  // Combine essential and non-essential data
  const combinedData = useMemo(() => {
    if (type === 'home' && nonEssentialData && !isLoadingNonEssential) {
      if (!nonEssentialData.categories) {
        return initialData;
      }

      // Get categories from both sources with proper type casting
      const initialCategories = (initialData as CatalogViewData).categories || {};
      const nonEssentialCategories = nonEssentialData.categories || {};

      // Use type casting to handle dynamic string keys
      const mergedCategories: Record<string, any> = { ...initialCategories };

      // For each category in non-essential data
      Object.keys(nonEssentialCategories).forEach((key) => {
        const typedKey = key as keyof typeof initialCategories;

        if (!initialCategories[typedKey]) {
          // If the category doesn't exist in initial data, add it
          mergedCategories[key] =
            nonEssentialCategories[key as keyof typeof nonEssentialCategories];
        } else {
          // If the category exists in both, merge the items
          const initialItems = initialCategories[typedKey]?.items || [];
          const nonEssentialItems =
            nonEssentialCategories[key as keyof typeof nonEssentialCategories]?.items || [];

          // If non-essential items are not empty, use them, otherwise keep initial items
          const mergedItems = nonEssentialItems.length > 0 ? nonEssentialItems : initialItems;

          mergedCategories[key] = {
            ...initialCategories[typedKey],
            items: mergedItems,
          };
        }
      });

      return {
        ...initialData,
        categories: mergedCategories,
      };
    }
    return initialData;
  }, [initialData, nonEssentialData, isLoadingNonEssential, type]);

  // Handle redirect events from catalog screens
  const handleRedirect = useCallback(
    (url: string) => {
      // Check if leaving catalog section
      const isCatalogSection = url.startsWith('/catalogo') || url.startsWith('/catalog');

      // If navigating away from catalog, invalidate caches
      if (!isCatalogSection && user?.email) {
        invalidateCaches();
      }

      if (url.startsWith('/')) {
        router.push(url);
      } else if (url.startsWith('http')) {
        // If it's an external URL, use window.location
        window.location.href = url;
      } else {
        // If it doesn't have a protocol, assume it's a relative path
        router.push(`/${url}`);
      }
    },
    [router, user?.email],
  );

  // Handle back navigation
  const handleBackNavigation = useCallback(() => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push('/catalogo');
    }
  }, [router]);

  // Handle navigation to different catalog sections
  const handleNavigate = useCallback(
    (data: { slug: string; isExternalPage?: boolean }) => {
      const { slug, isExternalPage } = data;
      if (isExternalPage) {
        window.open(slug, '_blank', 'noopener,noreferrer');
      } else {
        router.push(slug);
      }
    },
    [router],
  );

  // Handle search navigation
  const handleSearch = useCallback(
    (data: { query?: string; filters: Record<string, any>; page?: any }) => {
      if (data.query) {
        router.push(`/catalogo/ricerca?q=${encodeURIComponent(data.query)}`);
      }
    },
    [router],
  );

  // Handle category navigation
  const handleCategory = useCallback(
    (data: { categoryName?: string; filters: Record<string, any>; page?: any }) => {
      if (data.categoryName) {
        router.push(`/catalogo/categoria/${data.categoryName}`);
      }
    },
    [router],
  );

  // Set up user authentication state
  useEffect(() => {
    if (isReady) return;
    setHydrated(true);
    setIsReady(true);
  }, [setHydrated, isReady]);

  // Set up event listeners
  useEffect(() => {
    catalogEventBus.on(CatalogEventKeys.redirects.to, handleRedirect);
    catalogEventBus.on(CatalogEventKeys.navigateBack.do, handleBackNavigation);
    catalogEventBus.on(CatalogEventKeys.navigate.do, handleNavigate);
    catalogEventBus.on(CatalogEventKeys.search.do, handleSearch);
    catalogEventBus.on(CatalogEventKeys.category.do, handleCategory);

    return () => {
      catalogEventBus.off(CatalogEventKeys.redirects.to, handleRedirect);
      catalogEventBus.off(CatalogEventKeys.navigateBack.do, handleBackNavigation);
      catalogEventBus.off(CatalogEventKeys.navigate.do, handleNavigate);
      catalogEventBus.off(CatalogEventKeys.search.do, handleSearch);
      catalogEventBus.off(CatalogEventKeys.category.do, handleCategory);
    };
  }, [
    catalogEventBus,
    handleRedirect,
    handleBackNavigation,
    handleNavigate,
    handleSearch,
    handleCategory,
  ]);

  // Check if catalog type has changed
  useEffect(() => {
    if (prevType && prevType !== type) {
      if (user?.email) {
        invalidateCaches({ filters: true });
      }
    }

    setPrevType(type);
  }, [type, prevType, user?.email]);

  // Show loading state while preparing
  if (!isReady) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton animation="wave" variant="rounded" height={150} className="rounded-xl" />
        <Skeleton animation="wave" variant="rounded" height={450} className="rounded-xl" />
      </div>
    );
  }

  // Render the appropriate screen based on type
  if (type === 'home') {
    return (
      <CatalogAppWrapper eventBus={catalogEventBus}>
        <CatalogHomeScreen
          dict={dict}
          eventBus={catalogEventBus}
          initialData={combinedData as CatalogViewData}
          isLoggedIn={isLoggedIn}
          isLoadingNonEssential={isLoadingNonEssential}
        />
      </CatalogAppWrapper>
    );
  }

  if (type === 'search' && query) {
    return (
      <CatalogAppWrapper eventBus={catalogEventBus}>
        <CatalogSearchScreen
          dict={dict}
          eventBus={catalogEventBus}
          initialData={initialData as CatalogSearchInitialData}
          query={query}
          isLoggedIn={isLoggedIn}
        />
      </CatalogAppWrapper>
    );
  }

  if (type === 'category') {
    return (
      <CatalogAppWrapper eventBus={catalogEventBus}>
        <CatalogCategoryScreen
          dict={dict}
          eventBus={catalogEventBus}
          initialData={initialData as CatalogCategoryInitialData}
          categoryName={categoryName}
          isLoggedIn={isLoggedIn}
        />
      </CatalogAppWrapper>
    );
  }

  // Fallback for invalid type
  return (
    <div className="flex flex-col gap-5">
      <Skeleton animation="wave" variant="rounded" height={150} className="rounded-xl" />
      <Skeleton animation="wave" variant="rounded" height={450} className="rounded-xl" />
    </div>
  );
}

export default CatalogContainer;
